<template>
  <div class="offcanvas" tabindex="-1">
    <Teleport :to="teleportTo">
      <Transition name="slide-fade" @leave="handleLeave" @enter="handleEnter">
        <aside
          v-if="isOpen"
          id="offcanvas-id-example"
          ref="offcanvasRef"
          :class="populateClass"
          :style="cssVars"
          role="dialog"
          :aria-modal="isOpen"
        >
          <slot />
        </aside>
      </Transition>
      <div
        v-if="isOpen && backdrop"
        class="offcanvas-backdrop"
        aria-hidden="true"
        @click="close"
      ></div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import type { VariantOption } from "@/types/types";
import { onClickOutside, onKeyStroke } from "@vueuse/core";
import { computed, ref } from "vue";
const offcanvasRef = ref<HTMLElement | null>(null);

const emits = defineEmits<{
  (event: "update:open" | "update:close", value: boolean): void;
}>();
const isOpen = defineModel<boolean>({ required: true });
type Props = {
  direction: "left" | "right";
  width?: `${number}${"px" | "vw" | "%"}`;
  bgVariant?: VariantOption;
  teleportTo?: string;
  backdrop?: boolean;
  shadow?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  direction: "right",
  width: "40vw",
  bgVariant: "white",
  teleportTo: "body",
  backdrop: false,
  shadow: true
});
const populateClass = computed(() => {
  const classes = [
    "offcanvas",
    "shadow",
    "position-fixed",
    `bg-${props.bgVariant}`,
    "text-dark",
    "top-0",
    props.direction === "right" ? "end-0" : "start-0"
  ];
  return classes;
});

const cssVars = computed<Record<string, string>>(() => ({
  "--offcanvas-width": props.width,
  "--offcanvas-direction": props.direction === "right" ? "100%" : "-100%"
}));

const close = () => {
  isOpen.value = false;
};
const handleEnter = () => {
  emits("update:open", isOpen.value);
};
const handleLeave = (el: Element) => {
  (el as HTMLElement).style.transform = `translateX(${props.direction === "right" ? "" : "-"}100%)`;
  (el as HTMLElement).style.transition = "transform 0.3s ease-in-out";
  emits("update:close", isOpen.value);
};

onClickOutside(offcanvasRef, close);
onKeyStroke("Escape", (e) => {
  if (isOpen.value) {
    e.preventDefault();
    close();
  }
});
</script>

<style scoped>
.offcanvas {
  height: 100vh;
  width: var(--offcanvas-width);
  display: flex;
  flex-direction: column;
  max-width: 100%;
  height: 100%;
  margin: 0;
  outline: 0;
  z-index: var(--zindex-offcanvas);
}
.slide-fade-enter-active {
  transition: transform 0.2s ease;
}

.slide-fade-enter-from {
  transform: translateX(var(--offcanvas-direction));
}

.offcanvas-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: var(--zindex-offcanvas-backdrop);
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
